import type { RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

/**
 * Detects when an element's content overflows its box
 * and updates on size changes
 * - Uses ResizeObserver when available, with a window resize fallback otherwise
 * - Returns a ref to attach to the observed element
 * and a boolean isOverflowed flag
 */
export function useOverflowObserver<T extends HTMLElement>(
  deps: unknown[] = []
): [RefObject<T | null>, boolean] {
  const ref = useRef<T | null>(null);
  const [isOverflowed, setIsOverflowed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const recalc = () => {
      const overflow = el.scrollWidth > el.offsetWidth;
      setIsOverflowed(overflow);
    };

    recalc();

    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', recalc);
    } else {
      ro = new ResizeObserver(() => {
        recalc();
      });
      ro.observe(el);
    }

    return () => {
      if (ro) {
        ro.disconnect();
      } else {
        window.removeEventListener('resize', recalc);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return [ref, isOverflowed];
}
